import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./cancellation.css";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { REACT_APP_API_URL } from "../../../../../env";
import { useFetchCancellationPolicyQuery } from "../../../../../api/fetchCancellationPolicy";

const cancellationPolicy = [
  {
    id: 1,
    window: "15 or more days",
    deduction: "20%",
    description: "20% deduction if cancelled 15 or more days before check-in date",
  },
  {
    id: 2,
    window: "7 to 14 days",
    deduction: "30%",
    description:
      "30% deduction if cancelled between 7 to 14 days before check-in date",
  },
  {
    id: 3,
    window: "2 to 6 days",
    deduction: "50%",
    description:
      "50% deduction if cancelled between 2 to 6 days before check-in date",
  },
  {
    id: 4,
    window: "Less than 2 days",
    deduction: "No cancellation allowed",
    description: "No cancellation allowed within 2 days of check-in date",
    isCritical: true,
  },
];

const Cancellation = () => {
  const { P_intBookingID } = useParams();
  const { user } = useSelector((state) => state.auth);
  const userId = user?.user?.customerId;
  console.log('userId', userId)
  const [cancellationType, setCancellationType] = useState("");
  const [partialCancellationTable, setPartialCancellationTable] = useState([]);
  const { data, error, isLoading } =
    useFetchCancellationPolicyQuery(P_intBookingID);
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [originalRooms, setOriginalRooms] = useState([]);
  const [workingRooms, setWorkingRooms] = useState([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [cancellationAmount, setCancellationAmount] = useState(0);
  const [isCancelling, setIsCancelling] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedRoomConfig, setSelectedRoomConfig] = useState(null);
  const [selectedRoomID, setSelectedRoomID] = useState(null);
  const [cancelledRooms, setCancelledRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [cancelledRoomIds, setCancelledRoomIds] = useState([]);
  const [partialCancellationStarted, setPartialCancellationStarted] = useState(false);
  const [partialDayDates, setPartialDayDates] = useState({});

  // API-driven state for locking dropdown
  const [hasPartialRoomCancellation, setHasPartialRoomCancellation] = useState(false);
  const [isDateModified, setIsDateModified] = useState(false);

  // Check localStorage for partial cancellation flag
  useEffect(() => {
    const flag = localStorage.getItem(`partialCancellationStarted_${P_intBookingID}`);
    if (flag === "true") {
      setPartialCancellationStarted(true);
    }
  }, [P_intBookingID]);

  // lock flags to dropdown from API (useFetchCancellationPolicyQuery)
  useEffect(() => {
    if (data) {
      
      if (data.hasPartialDayCancellation === true) {
        setIsDateModified(true);
      }

      if (data.hasPartialRoomCancellation === true) {
        
        setHasPartialRoomCancellation(true);
      }
    }
  }, [data]);

  useEffect(() => {
    const fetchCancellationBtn = async () => {
      try {
        const response = await axios.get(
          `${REACT_APP_API_URL}api/hotel/get-cancellation-details/${P_intBookingID}`
        );
      //console.log("Cancellation Details API Response:", response.data);

        const roomIds = response.data
          .map((item) =>
            item.vchRoomIds
              ? item.vchRoomIds.split(",").map((id) => parseInt(id.trim()))
              : []
          )
          .flat();

        setCancelledRoomIds(roomIds);
      } catch (error) {
        console.error("Error fetching cancellation details:", error);
      }
    };

    fetchCancellationBtn();
  }, [P_intBookingID]);

  useEffect(() => {
    if (data?.booking) {
      const allRooms = data.booking.flatMap((booking, bookingIndex) =>
        Array.from({ length: booking.TotalRoom }, (_, roomIndex) => ({
          ...booking,
          uniqueId: `${bookingIndex}-${roomIndex}`,
        }))
      );
      setOriginalRooms(allRooms);
      setWorkingRooms(allRooms);
      const modified = data.booking.some(b => b.ModificationCount > 0);
      if (modified) setIsDateModified(true);
    }
  }, [data]);

  // Check if booking has more than 1 day duration
  const hasMultipleDays = () => {
    if (!originalRooms || originalRooms.length === 0) return false;

    const firstRoom = originalRooms[0];
    if (!firstRoom.CheckinDate || !firstRoom.CheckOutDate) return false;

    const checkin = new Date(firstRoom.CheckinDate);
    const checkout = new Date(firstRoom.CheckOutDate);
    if (isNaN(checkin.getTime()) || isNaN(checkout.getTime())) return false;

    const diffTime = checkout - checkin;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 1;
  };

  // Determine GST rate based on booking date
  const getGSTRate = (bookingDate) => {
    const cutoffDate = new Date('2022-09-22');
    const booking = new Date(bookingDate);
    return booking < cutoffDate ? 0.12 : 0.05;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateTotal = (room) => {
    if (!room.CheckinDate || !room.CheckOutDate) {
      return 0;
    }
    const checkin = new Date(room.CheckinDate);
    const checkout = new Date(room.CheckOutDate);
    if (isNaN(checkin.getTime()) || isNaN(checkout.getTime())) {
      return 0;
    }
    const diffTime = checkout - checkin;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return room.PricePerDay * (diffDays > 0 ? diffDays : 0) + room.ExtraPersonRate * (diffDays > 0 ? diffDays : 0);
  };

  // Calculate GST breakdown for a room
  const calculateGSTBreakdown = (room) => {
    // Calculate nights
    const checkin = new Date(room.CheckinDate);
    const checkout = new Date(room.CheckOutDate);
    const diffTime = checkout - checkin;
    const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const baseAmount = calculateTotal(room);
    const gstRate = getGSTRate(room.BookingDate);
    const gstAmount = baseAmount * gstRate;
    const totalWithGST = baseAmount + gstAmount;

    return {
      baseAmount,
      gstRate: gstRate * 100,
      gstAmount,
      totalWithGST,
      nights,
      pricePerDay: room.PricePerDay,
      extraPersonRate: room.ExtraPersonRate
    };
  };


  // Handle errors from the query
  const handleCancellationTypeChange = async (type) => {
    setCancellationType(type);
    setWorkingRooms(originalRooms);
    if (type !== "full") {
      try {
        const response = await axios.post(
          `${REACT_APP_API_URL}api/hotel/cancel/partial/${P_intBookingID}`
        );
        setPartialCancellationTable(response.data);
      } catch (error) {
        // toast.error("Error while fetching cancellation policy");
      }
    }
    if (type === "partial") {
      localStorage.setItem(`partialCancellationStarted_${P_intBookingID}`, "true");
      setPartialCancellationStarted(true);
    }
  };

  const handleRemoveRoom = (roomId) => {
    setWorkingRooms((prev) => prev.filter((room) => room.uniqueId !== roomId));
  };

  // Function to handle full cancellation 
  const handleFullCancellation = async () => {
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/hotel/cancel-all/${P_intBookingID}`
      );
      if (response.status === 200) {
        const cancelledText =
          workingRooms.length > 1
            ? "All rooms cancelled successfully"
            : "Room cancelled successfully";
        alert(cancelledText);

        setCancelledRooms(originalRooms.map((room) => room.RoomType));
        setWorkingRooms([]);
        setCancelledRoomIds(originalRooms.map((room) => room.RoomType));

        window.location.reload();
      }
    } catch (error) {
      //   toast.error("Error while cancelling booking");
    } finally {
      setShowModal(false);
    }
  };

  // Function to handle partial cancellation 
  const handleRoomCancellation = async (price, roomconfig, ROOMID) => {
    setSelectedPrice(price);
    setSelectedRoomConfig(roomconfig);
    setSelectedRoomID(ROOMID);
    setShowCancelModal(true);

    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/hotel/cancel/partial/${P_intBookingID}`,
        {
          Price: price,
          RoomConfig: roomconfig,
          UserID: userId,
          ROOMID: ROOMID,
        }
      );

      if (response.status === 200) {
        const cancelAmt = response.data.cancellationData[0].Amount;
        setCancellationAmount(cancelAmt);
        setRefundAmount(price + price * 0.05 - cancelAmt);
      }
    } catch (error) {
      //   toast.error("Error while calculating cancellation charges");
    }
  };

  // Function to confirm cancellation
  const confirmCancellation = async () => {
    console.log('cancelling room with ID:', selectedRoomID);
    if (!selectedPrice || !selectedRoomConfig || !userId) {
      console.log(selectedPrice, selectedRoomConfig, userId);
      console.error("Missing data for cancellation confirmation");
      return;
    }

    try {
      const cancelResponse = await axios.post(
        `${REACT_APP_API_URL}api/hotel/cancel-room/confirm/${P_intBookingID}`,
        {
          Price: selectedPrice,
          RoomConfig: selectedRoomConfig,
          UserID: userId,
          ROOMID: selectedRoomID,
        }
      );
      console.log('cancelResponse', cancelResponse);

      if (cancelResponse.status === 200) {
        setCancelledRoomIds((prev) => [...prev, parseInt(selectedRoomConfig)]);
        setWorkingRooms((prev) =>
          prev.filter((room) => room.RoomBookingID !== selectedRoomID)
        );

        try {
          const response = await axios.get(
            `${REACT_APP_API_URL}api/hotel/get-cancellation-details/${P_intBookingID}`
          );
          const roomIds = response.data
            .map((item) =>
              item.vchRoomIds
                ? item.vchRoomIds.split(",").map((id) => parseInt(id.trim()))
                : []
            )
            .flat();
          setCancelledRoomIds(roomIds);
        } catch (error) {
          console.error("Error fetching updated cancellation details:", error);
        }

        const remainingRooms = workingRooms.length - 1;
        const successMessage =
          remainingRooms > 0
            ? "Room cancelled successfully"
            : "All rooms cancelled successfully";

        // toast.success(successMessage);
        setPartialCancellationStarted(true);
        localStorage.setItem(`partialCancellationStarted_${P_intBookingID}`, "true");

        window.location.reload();
      }

      setShowCancelModal(false);
    } catch (error) {
      // console.error("Error in confirmation:", error);
      //   toast.error("Error while confirming cancellation");
    }
  };

  // Handle partial day date changes (Global)
  const handlePartialDayDateChange = (_, field, value) => {
    setPartialDayDates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateAllBookings = async () => {
    if (workingRooms.length === 0) return;

    const firstRoom = workingRooms[0];
    const checkinDateToUse = partialDayDates.newCheckin || formatDateForInput(firstRoom.CheckinDate);
    const checkoutDateToUse = partialDayDates.newCheckout || formatDateForInput(firstRoom.CheckOutDate);

    const formatDateToDMY = (dateStr) => {
      const date = new Date(dateStr);
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const day = String(date.getDate()).padStart(2, '0');
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const newCheckinDMY = formatDateToDMY(checkinDateToUse);
    const newCheckoutDMY = formatDateToDMY(checkoutDateToUse);

    try {
      const updatePromises = workingRooms.map(room =>
        axios.post(
          `${REACT_APP_API_URL}api/hotel/modify-booking/${P_intBookingID}`,
          {
            newCheckinDate: newCheckinDMY,
            newCheckoutDate: newCheckoutDMY,
            roomBookingID: room.RoomBookingID,
            userID: userId
          }
        )
      );

      await Promise.all(updatePromises);

      alert("Booking dates updated successfully!");
      window.location.reload();

    } catch (error) {
      console.error("Error updating bookings:", error);
      const errorMessage = error.response?.data?.message || "Failed to update booking dates. Please try again.";
      alert(errorMessage);
    }
  };

  // Enforce lock based on API flags ONLY
  useEffect(() => {
    if (hasPartialRoomCancellation) {
      // API shows Partial Room Cancellation
      setCancellationType("partial");
    } else if (isDateModified) {
      // API shows Partial Day Cancellation (Modification)
      setCancellationType("partial-day");
    }
    // If both are false, user can select any option
  }, [hasPartialRoomCancellation, isDateModified]);

  return (
    <div id="cancellation-page" style={{ marginTop: ".5rem" }}>
      <div
        className="bg-white p-6 rounded-lg shadow-md p-15"
        style={{ padding: "10px" }}
      >
        <h2
          className="text-xl font-bold mb-6 text-primary-600 text-center"
          style={{ marginTop: "10px", marginBottom: "20px" }}
        >
          Hotel Booking Cancellation
        </h2>

        {/* Cancellation Policy Section */}
        <div className="mb-6 bg-primary-50 p-2 rounded-lg border border-primary-200">
          {/* CANCELLATION TYPE DROPDOWN with LABEL */}
          <div
            className="d-flex flex-column align-items-center justify-content-center mb-4"
            style={{ marginTop: "10px" }}
          >
            <label
              htmlFor="cancelTypeDropdown"
              className="form-label fw-bold mb-2 text-primary"
            >
              Cancellation Type:
            </label>
            <select
              id="cancelTypeDropdown"
              className="form-select w-auto"
              value={cancellationType}
              onChange={(e) => handleCancellationTypeChange(e.target.value)}
              // Disable dropdown if locked by DB flags
              disabled={hasPartialRoomCancellation}
            >
              <option value="" disabled>
                Select an option
              </option>{" "}
              {/* Optional blank default */}

              {/* Full Cancellation: Allowed unless partial room cancellation has started */}
              {!hasPartialRoomCancellation && (
                <option value="full">Full Cancellation</option>
              )}

              {/* Partial Cancellation: If room cancellation OR (multiple rooms AND not date modified) */}
              {(hasPartialRoomCancellation || (originalRooms.length > 1 && !isDateModified)) && (
                <option value="partial">Partial Cancellation</option>
              )}

              {/* Partial Day Cancellation: If date modified OR (multiple days AND not room cancelled) */}
              {(isDateModified || (hasMultipleDays() && !hasPartialRoomCancellation)) && (
                <option value="partial-day">Partial Day Cancellation</option>
              )}
            </select>

            {/* Display Lock Message */}
            {(hasPartialRoomCancellation || cancelledRoomIds.length > 0 || isDateModified) && (
              <div className="mt-2 text-danger small fw-bold">
                {(hasPartialRoomCancellation || cancelledRoomIds.length > 0)
                  ? "Restricted to Partial Cancellation as some rooms are already cancelled."
                  : "Partial room cancellation is not available after date modification. Full cancellation is still allowed."}
              </div>
            )}
          </div>

          <h5 className="text-lg font-semibold mb-2">Cancellation Policy</h5>
          {/* DESKTOP CANCELLATION POLICY TABLE */}
          <div className="d-none d-md-block">
            <table className="table table-bordered cancellation-table">
              <thead>
                <tr>
                  <th>Sl#</th>
                  <th>Cancellation Window</th>
                  <th>Deduction</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {cancellationPolicy.map((policy) => (
                  <tr key={policy.id}>
                    <td>{policy.id}</td>
                    <td>{policy.window}</td>
                    <td style={policy.isCritical ? { color: "red" } : {}}>
                      {policy.deduction}
                    </td>
                    <td style={policy.isCritical ? { color: "red" } : {}}>
                      {policy.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CANCELLATION POLICY CARDS */}
          <div className="d-block d-md-none">
            {cancellationPolicy.map((policy) => (
              <div key={policy.id} className="card mb-3 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-2">#{policy.id}</h5>
                  <p>
                    <strong>Window:</strong> {policy.window}
                  </p>
                  <p>
                    <strong>Deduction:</strong>{" "}
                    <span style={policy.isCritical ? { color: "red" } : {}}>
                      {policy.deduction}
                    </span>
                  </p>
                  <p>
                    <strong>Description:</strong>{" "}
                    <span style={policy.isCritical ? { color: "red" } : {}}>
                      {policy.description}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {cancellationType ? (
          // SHOW all content when cancellationType is selected
          <div className="rounded-3 mt-4">
            <h3 className="text-center p-5 mt-2">
              {cancellationType === "partial"
                ? "Select a Room to Cancel"
                : cancellationType === "partial-day"
                  ? "Modify Booking Dates"
                  : "Your Booking Overview"}
            </h3>

            {/* Global Date Pickers for Partial Day Cancellation */}
            {cancellationType === "partial-day" && workingRooms.length > 0 && (
              <div className="row justify-content-center mb-4">
                <div className="col-md-10">
                  <div className="card shadow-sm border-primary">
                    <div className="card-body bg-light">
                      <h5 className="card-title text-center mb-3 text-primary">
                        Select New Dates for All Rooms
                      </h5>
                      <div className="row g-3 align-items-center justify-content-center">

                        <div className="col-md-3 col-6">
                          <label className="form-label small fw-bold text-muted">Old Check-in</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formatDateForInput(workingRooms[0].CheckinDate)}
                            disabled
                          />
                        </div>

                        <div className="col-md-3 col-6">
                          <label className="form-label small fw-bold text-dark">New Check-in</label>
                          <input
                            type="date"
                            className="form-control border-primary"
                            value={partialDayDates.newCheckin || ''}
                            onChange={(e) => handlePartialDayDateChange(null, 'newCheckin', e.target.value)}
                            min={formatDateForInput(workingRooms[0].CheckinDate)}
                            max={(() => {
                              if (partialDayDates.newCheckout) {
                                const maxDate = new Date(partialDayDates.newCheckout);
                                maxDate.setDate(maxDate.getDate() - 1);
                                return formatDateForInput(maxDate);
                              }
                              const maxDate = new Date(workingRooms[0].CheckOutDate);
                              maxDate.setDate(maxDate.getDate() - 1);
                              return formatDateForInput(maxDate);
                            })()}
                          />
                        </div>

                        <div className="col-md-3 col-6">
                          <label className="form-label small fw-bold text-muted">Old Check-out</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formatDateForInput(workingRooms[0].CheckOutDate)}
                            disabled
                          />
                        </div>

                        <div className="col-md-3 col-6">
                          <label className="form-label small fw-bold text-dark">New Check-out</label>
                          <input
                            type="date"
                            className="form-control border-primary"
                            value={partialDayDates.newCheckout || ''}
                            onChange={(e) => handlePartialDayDateChange(null, 'newCheckout', e.target.value)}
                            min={(() => {
                              if (partialDayDates.newCheckin) {
                                const minDate = new Date(partialDayDates.newCheckin);
                                minDate.setDate(minDate.getDate() + 1);
                                return formatDateForInput(minDate);
                              }
                              const minDate = new Date(workingRooms[0].CheckinDate);
                              minDate.setDate(minDate.getDate() + 1);
                              return formatDateForInput(minDate);
                            })()}
                            max={formatDateForInput(workingRooms[0].CheckOutDate)}
                          />
                        </div>
                      </div>

                      <div className="mt-4 text-center">
                        <button
                          className="btn btn-primary px-4 fw-bold"
                          onClick={handleUpdateAllBookings}
                        >
                          Update Booking Dates
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Table */}
            <div className="d-none d-md-block">
              <table className="table table-bordered w-100 cancellation-table">
                <thead>
                  <tr>
                    <th>Sl.No</th>
                    <th>Room Type</th>
                    <th>No.Of Rooms</th>
                    <th>No. of Adults</th>
                    <th>No. of Children</th>
                    <th>Price Per Day</th>
                    <th>Extra Person Rate</th>
                    <th>Extra Person</th>
                    <th>Base Amount</th>
                    <th>GST ({workingRooms[0] && getGSTRate(workingRooms[0].BookingDate) * 100}%)</th>
                    <th>Total Amount</th>
                    {cancellationType === "partial" && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {workingRooms.map((room, index) => {
                    const breakdown = calculateGSTBreakdown(room);
                    return (
                      <tr key={room.uniqueId}>
                        <td>{index + 1}</td>
                        <td>{room.RoomTypeName[0]}</td>
                        <td>1</td>
                        <td>{room.NoOfAdult}</td>
                        <td>{room.NoOfChild}</td>
                        <td>₹{room.PricePerDay}</td>
                        <td>₹{room.ExtraPersonRate}</td>
                        <td>{room.AllowedExtraPerson}</td>
                        <td>₹{breakdown.baseAmount.toFixed(2)}</td>
                        <td>₹{breakdown.gstAmount.toFixed(2)}</td>
                        <td>
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            ₹{breakdown.totalWithGST.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            ({breakdown.pricePerDay} × {breakdown.nights} nights) + ({breakdown.extraPersonRate} × {breakdown.nights}) + GST
                          </div>
                        </td>
                        {cancellationType === "partial" && (
                          <td>
                            {cancelledRoomIds.includes(
                              parseInt(room.RoomType)
                            ) ? (
                              <button className="btn btn-secondary" disabled>
                                Cancelled
                              </button>
                            ) : (
                              <button
                                className="btn btn-danger"
                                onClick={() =>
                                  handleRoomCancellation(
                                    breakdown.baseAmount,
                                    room.RoomType,
                                    room.RoomBookingID
                                  )
                                }
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {workingRooms.map((room, index) => {
                const breakdown = calculateGSTBreakdown(room);
                return (
                  <div className="card mb-3 shadow-sm" key={room.uniqueId}>
                    <div className="card-body">
                      <h5 className="card-title">Room {index + 1}</h5>
                      <p>
                        <strong>Room Type:</strong> {room.RoomTypeName[0]}
                      </p>
                      <p>
                        <strong>No. of Rooms:</strong> 1
                      </p>
                      <p>
                        <strong>No. of Adults:</strong> {room.NoOfAdult}
                      </p>
                      <p>
                        <strong>No. of Children:</strong> {room.NoOfChild}
                      </p>
                      <p>
                        <strong>Price Per Day:</strong> ₹{room.PricePerDay}
                      </p>
                      <p>
                        <strong>Extra Person Rate:</strong> ₹
                        {room.ExtraPersonRate}
                      </p>
                      <p>
                        <strong>Extra Person:</strong> {room.AllowedExtraPerson}
                      </p>
                      <p>
                        <strong>Base Amount:</strong> ₹{breakdown.baseAmount.toFixed(2)}
                      </p>
                      <p>
                        <strong>GST ({breakdown.gstRate}%):</strong> ₹{breakdown.gstAmount.toFixed(2)}
                      </p>
                      <p>
                        <strong>Total Amount:</strong> ₹{breakdown.totalWithGST.toFixed(2)}
                        <br />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          ({breakdown.pricePerDay} × {breakdown.nights} nights) + ({breakdown.extraPersonRate} × {breakdown.nights}) + GST
                        </small>
                      </p>

                      {cancellationType === "partial" &&
                        (cancelledRoomIds.includes(parseInt(room.RoomType)) ? (
                          <button
                            className="btn btn-secondary w-100 mt-2"
                            disabled
                          >
                            Cancelled
                          </button>
                        ) : (
                          <button
                            className="btn btn-danger w-100 mt-2"
                            onClick={() =>
                              handleRoomCancellation(
                                breakdown.baseAmount,
                                room.RoomType,
                                room.RoomBookingID
                              )
                            }
                          >
                            Cancel
                          </button>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Full Cancellation Button */}
            {cancellationType === "full" &&
              workingRooms.length > 0 &&
              !hasPartialRoomCancellation && (
                <div className="mt-4 d-flex justify-content-center">
                  <button
                    onClick={() => setShowModal(true)}
                    className="btn btn-danger"
                    style={{ minWidth: "200px" }}
                  >
                    {workingRooms.length > 1
                      ? "Cancel All Rooms"
                      : "Cancel Room"}
                  </button>
                </div>
              )}
          </div>
        ) : (
          <em></em>
        )}
      </div>

      {/* Full Cancellation Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Booking Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: "10px", fontSize: "15px", color: "#000" }}>
            You are about to cancel{" "}
            <strong>
              {workingRooms.length > 1 ? "all rooms" : "this room"}
            </strong>{" "}
            in this booking.
          </p>
          <p style={{ fontSize: "13px", color: "#777" }}>
            Are you sure you want to proceed with cancellation?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Go Back
          </Button>
          <Button variant="danger" onClick={handleFullCancellation}>
            Yes, Cancel {workingRooms.length > 1 ? "All" : "Room"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Partial Cancellation Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Room Cancellation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ marginBottom: "10px", fontSize: "14px", color: "#555" }}>
            Total amount paid:{" "}
            <strong>₹{(selectedPrice * 1.05).toFixed(2)}</strong> (includes 5%
            GST)
          </p>
          <p style={{ fontSize: "15px", color: "#000" }}>
            Cancellation charge:{" "}
            <strong style={{ color: "#e74c3c" }}>₹{cancellationAmount}</strong>
          </p>
          <p style={{ fontSize: "15px", color: "#000" }}>
            Refund amount:{" "}
            <strong style={{ color: "green" }}>₹{refundAmount}</strong>
          </p>
          <p style={{ marginTop: "15px", fontSize: "13px", color: "#777" }}>
            Are you sure you want to proceed with cancelling this room?
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Go Back
          </Button>
          <Button variant="danger" onClick={confirmCancellation}>
            Yes, Cancel Room
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default Cancellation;
